import { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Alert,
  App,
  type TableColumnsType,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { formatRupees } from '../../lib/money';
import {
  EMPTY_PKG_FORM,
  bodyFromForm,
  coinsPerRupee,
  formFromPackage,
  packageFormError,
  previewDiscountPercent,
  type AdminPackage,
  type PkgForm,
} from './coinPackageForm';
import { PaymentModeCard } from './PaymentModeCard';

function errMsg(e: unknown, fallback: string) {
  return (
    (e as { response?: { data?: { message?: string } } }).response?.data
      ?.message ?? fallback
  );
}

export function CoinPackagesPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminPackage | null>(null);
  const [form, setForm] = useState<PkgForm>(EMPTY_PKG_FORM);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'coin-packages'],
    queryFn: async () =>
      (await api.get<{ packages: AdminPackage[] }>('/admin/coin-packages')).data
        .packages,
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = bodyFromForm(form);
      if (editing) await api.put(`/admin/coin-packages/${editing.id}`, body);
      else await api.post('/admin/coin-packages', body);
    },
    onSuccess: () => {
      message.success(editing ? 'Package updated' : 'Package created');
      qc.invalidateQueries({ queryKey: ['admin', 'coin-packages'] });
      setOpen(false);
    },
    onError: (e) => message.error(errMsg(e, 'Could not save package')),
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post(`/admin/coin-packages/${id}/${active ? 'activate' : 'deactivate'}`),
    onSuccess: () => {
      message.success('Updated');
      qc.invalidateQueries({ queryKey: ['admin', 'coin-packages'] });
    },
    onError: (e) => message.error(errMsg(e, 'Could not update')),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_PKG_FORM);
    setOpen(true);
  };
  const openEdit = (row: AdminPackage) => {
    setEditing(row);
    setForm(formFromPackage(row));
    setOpen(true);
  };

  const columns: TableColumnsType<AdminPackage> = [
    {
      title: 'Package',
      dataIndex: 'name',
      render: (n: string, row) => (
        <Space>
          <span style={{ fontWeight: 600 }}>{n}</span>
          {row.isPopular && (
            <Tag color="magenta" icon={<StarFilled />}>
              popular
            </Tag>
          )}
        </Space>
      ),
    },
    {
      // Same three numbers the app shows: paid, bonus, total.
      title: 'Coins (paid + bonus = total)',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          <span>
            Paid <strong>{row.coins.toLocaleString('en-IN')}</strong>
          </span>
          <span style={{ color: '#22A06B' }}>
            Bonus +{row.bonusCoins.toLocaleString('en-IN')}
          </span>
          <span style={{ fontWeight: 700, color: '#FFD06A' }}>
            Total {row.totalCoins.toLocaleString('en-IN')}
          </span>
        </Space>
      ),
    },
    {
      title: 'Price (charged)',
      dataIndex: 'amountPaise',
      render: (p: string | number, row) => (
        <Space direction="vertical" size={2}>
          {row.originalPriceLabel && (
            <Typography.Text delete type="secondary" style={{ fontSize: 12 }}>
              {row.originalPriceLabel}
            </Typography.Text>
          )}
          <Tag color="gold">{row.priceLabel || formatRupees(p)}</Tag>
          {row.discountPercent != null && (
            <Tag color="red">{row.discountPercent}% OFF</Tag>
          )}
        </Space>
      ),
    },
    {
      // Informational only — price and coins are set independently, so this
      // is just a way to compare value across packages.
      title: 'Coins / ₹',
      render: (_: unknown, row) => {
        const r = coinsPerRupee(row.totalCoins, row.amountPaise);
        return r === null ? '—' : r.toLocaleString('en-IN', { maximumFractionDigits: 2 });
      },
      width: 90,
    },
    {
      title: 'Google Play product',
      dataIndex: 'googlePlayProductId',
      render: (id: string | null) =>
        id ? <Tag color="blue">{id}</Tag> : <Tag color="warning">not set</Tag>,
    },
    { title: 'Order', dataIndex: 'displayOrder', width: 70 },
    {
      title: 'Active',
      render: (_: unknown, row) =>
        row.isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            active
          </Tag>
        ) : (
          <Tag>inactive</Tag>
        ),
    },
    {
      title: 'Actions',
      render: (_: unknown, row) => (
        <Space>
          <Can permission="coin_packages.edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
          </Can>
          <Can permission="coin_packages.toggle">
            {row.isActive ? (
            <Popconfirm
              title="Hide this package from the app?"
              description="Users will no longer see it in the recharge screen."
              onConfirm={() => setActive.mutate({ id: row.id, active: false })}
              okButtonProps={{ danger: true }}
            >
              <Button size="small" icon={<PoweroffOutlined />}>
                Deactivate
              </Button>
            </Popconfirm>
          ) : (
            <Button
              size="small"
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => setActive.mutate({ id: row.id, active: true })}
            >
              Activate
            </Button>
          )}
          </Can>
        </Space>
      ),
    },
  ];

  const formError = packageFormError(form);
  const formBody = bodyFromForm(form);
  const formDiscount = previewDiscountPercent(
    formBody.amountPaise,
    formBody.originalAmountPaise,
  );
  const formRatio = coinsPerRupee(form.coins + form.bonusCoins, formBody.amountPaise);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Coin packages
        </Typography.Title>
        <Can permission="coin_packages.create">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New package
          </Button>
        </Can>
      </div>
      <Can permission="payment_mode.view">
        <PaymentModeCard />
      </Can>
      <Alert
        showIcon
        type="info"
        message="Price and coins are set independently"
        description="Type the ₹ offer price the buyer pays and the coins they receive — neither is derived from the other. After a verified payment the wallet receives paid + bonus coins. The optional original price is display-only (shown struck through, with the % off worked out from the two prices) and is never charged. Keep the offer price in step with the Google Play product price."
      />
      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25 }}
        />
      </Card>

      <Modal
        title={editing ? `Edit package — ${editing.name}` : 'New coin package'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => save.mutate()}
        okText={editing ? 'Save' : 'Create'}
        okButtonProps={{
          disabled: formError !== null,
          loading: save.isPending,
        }}
      >
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <div>
            <Typography.Text>Name</Typography.Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder="Popular"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Typography.Text>Offer price (₹) — charged</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={1}
                max={1_000_000}
                step={1}
                precision={2}
                prefix="₹"
                value={form.priceRupees}
                onChange={(v) => setForm({ ...form, priceRupees: v ?? 0 })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text>Original price (₹) — optional</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={1}
                max={1_000_000}
                step={1}
                precision={2}
                prefix="₹"
                placeholder="No discount"
                value={form.originalPriceRupees}
                onChange={(v) => setForm({ ...form, originalPriceRupees: v ?? null })}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Typography.Text>Paid coins</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={1}
                max={10_000_000}
                precision={0}
                value={form.coins}
                onChange={(v) => setForm({ ...form, coins: v ?? 1 })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text>Bonus coins</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={0}
                max={10_000_000}
                precision={0}
                value={form.bonusCoins}
                onChange={(v) => setForm({ ...form, bonusCoins: v ?? 0 })}
              />
            </div>
          </div>
          <Alert
            type={formError ? 'warning' : 'success'}
            showIcon={false}
            message={
              <span style={{ fontSize: 13 }}>
                {formError ?? (
                  <>
                    Charges <strong>{formatRupees(formBody.amountPaise)}</strong>
                    {formDiscount != null && (
                      <>
                        {' '}
                        (was <s>{formatRupees(formBody.originalAmountPaise)}</s>,{' '}
                        <strong>{formDiscount}% OFF</strong>)
                      </>
                    )}{' '}
                    · lands{' '}
                    <strong>
                      {(form.coins + form.bonusCoins).toLocaleString('en-IN')} coins
                    </strong>{' '}
                    in the wallet
                    {form.bonusCoins > 0
                      ? ` (${form.coins.toLocaleString('en-IN')} paid + ${form.bonusCoins.toLocaleString('en-IN')} bonus)`
                      : ''}
                    {formRatio !== null
                      ? ` · ${formRatio.toLocaleString('en-IN', { maximumFractionDigits: 2 })} coins per ₹1`
                      : ''}
                  </>
                )}
              </span>
            }
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Typography.Text>Display order</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={0}
                max={9999}
                value={form.displayOrder}
                onChange={(v) => setForm({ ...form, displayOrder: v ?? 0 })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text style={{ display: 'block' }}>Popular badge</Typography.Text>
              <Switch
                style={{ marginTop: 8 }}
                checked={form.isPopular}
                onChange={(v) => setForm({ ...form, isPopular: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Text style={{ display: 'block' }}>Active</Typography.Text>
              <Switch
                style={{ marginTop: 8 }}
                checked={form.isActive}
                onChange={(v) => setForm({ ...form, isActive: v })}
              />
            </div>
          </div>
          <div>
            <Typography.Text>Google Play product ID</Typography.Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder="coins_99"
              value={form.googlePlayProductId}
              onChange={(e) =>
                setForm({ ...form, googlePlayProductId: e.target.value })
              }
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Must match the in-app product ID created in Play Console. Required for
              this package to be buyable via Google Play Billing — leave blank if it
              isn't on Play yet.
            </Typography.Text>
          </div>
        </Space>
      </Modal>
    </Space>
  );
}
